import { getFilteredResultsFromProviders } from '../calculators/calculator-result-utility'
import { EnergyType, Quadrant } from '../calculators/EnergyFlow'
import { Provider } from '../calculators/providers/common/Provider'
import { HybridHeatPumpExtraOutputField } from '../calculators/providers/HybridHeatPumpProvider'
import {
  BatteryParameters,
  HybridHeatPumpParameters,
  InefficientParameters,
  PreheaterParameters,
  ProviderFragment,
  ProviderParametersFragment,
  ProviderType,
  SolarPanelParameters,
} from '../generated/graphql'
import { sum } from '../util/array-utility'
import {
  formatArea,
  formatCoefficient,
  formatEnergyCapacity,
  formatPercent,
  formatPower,
} from '../util/formatters'
import { ParameterType, providerProperties } from './provider-properties'
import {
  mapHybridHeatPumpKeyToSubCategory,
  solarPanelSubCategories,
} from './result-categories'

const formatEfficiency = (parameters: ProviderParametersFragment): string =>
  formatPercent((parameters as InefficientParameters).efficiency)

const formatSolarArea = (parameters: ProviderParametersFragment): string =>
  formatArea((parameters as SolarPanelParameters).area)

const formatSolarEfficiency = (
  parameters: ProviderParametersFragment,
): string => formatPercent((parameters as SolarPanelParameters).solarEfficiency)

const formatRelativePower = (parameters: ProviderParametersFragment): string =>
  formatPercent((parameters as PreheaterParameters).relativePower)

const formatCop = (parameters: ProviderParametersFragment): string =>
  formatCoefficient((parameters as { cop: number }).cop)

const formatEer = (parameters: ProviderParametersFragment): string =>
  formatCoefficient((parameters as { eer: number }).eer)

const formatEerFromCop = (parameters: ProviderParametersFragment): string =>
  formatCoefficient((parameters as { cop: number }).cop - 1)

const formatMaxPower = (parameters: ProviderParametersFragment): string =>
  formatPower((parameters as { maxPower?: number }).maxPower ?? Infinity)

const formatHeatingPower = (parameters: ProviderParametersFragment): string =>
  formatPower((parameters as HybridHeatPumpParameters).heatingPower)

const formatCapacity = (parameters: ProviderParametersFragment): string =>
  formatEnergyCapacity((parameters as BatteryParameters).capacity)

const mapParameterTypeToFormatter: Record<
  ParameterType,
  (parameters: ProviderParametersFragment) => string
> = {
  [ParameterType.Empty]: () => '-',
  [ParameterType.Efficiency]: formatEfficiency,
  [ParameterType.MaxPower]: formatMaxPower,
  [ParameterType.HeatingPower]: formatHeatingPower,
  [ParameterType.PreheaterPowerCoverage]: formatRelativePower,
  [ParameterType.Multiplier]: formatEer,
  [ParameterType.Cop]: formatCop,
  [ParameterType.Eer]: formatEer,
  [ParameterType.EerFromCop]: formatEerFromCop,
  [ParameterType.Spf]: () => 'Spf', // Handled separately
  [ParameterType.Area]: formatSolarArea,
  [ParameterType.SolarEfficiency]: formatSolarEfficiency,
  [ParameterType.Overproduction]: () => 'Overproduction', // Handled separately
  [ParameterType.EnergyCapacity]: formatCapacity,
}

/** Format hybrid heat pump seasonal performance factor for heating / cooling */
const formatHhpSpf = (provider: Provider, energyType: EnergyType): string => {
  const totalProducedField =
    energyType === EnergyType.Heating
      ? HybridHeatPumpExtraOutputField.TotalHeating
      : HybridHeatPumpExtraOutputField.TotalCooling

  const electricityRequiredToProduceField =
    energyType === EnergyType.Heating
      ? HybridHeatPumpExtraOutputField.ElectricityRequiredByHeating
      : HybridHeatPumpExtraOutputField.ElectricityRequiredByCooling

  const totalProduced = getFilteredResultsFromProviders(
    [provider],
    mapHybridHeatPumpKeyToSubCategory(totalProducedField),
  )
  const electricityRequiredToProduce = getFilteredResultsFromProviders(
    [provider],
    mapHybridHeatPumpKeyToSubCategory(electricityRequiredToProduceField),
  )

  return `${formatCoefficient(
    totalProduced.total / electricityRequiredToProduce.total,
  )} *`
}
/**
 * Solar panel overproduction is the total excess production of all panel systems in the building, regardless of connections.
 * It is calculated by summing the sold electricity and wasted overproduced electricity and showing the value
 * as percentage of the collected green energy total (all the solar panels
 * have the same overproduction value as their secondary parameter).
 * This differs greatly from other provider types,
 * but this specification was requested by the client.
 * */
const formatSolarPanelOverproduction = (providers: Provider[]): string => {
  const filtersWithResults = solarPanelSubCategories.map((filter) => ({
    ...filter,
    result: getFilteredResultsFromProviders(providers, filter),
  }))
  const collected = filtersWithResults.find(
    (filter) => filter.quadrant === Quadrant.FromEnvironment,
  )
  const collectedTotal = collected?.result.total ?? 0

  if (collectedTotal <= 0) {
    return '-'
  }

  const overproduction = sum(
    filtersWithResults
      .filter((filter) => filter.quadrant === Quadrant.Waste)
      .map((filter) => filter.result.total),
  )
  // Prevent formatting negative zero caused by floating point rounding error
  const overproductionTotal =
    Math.round(overproduction) === 0 ? 0 : overproduction

  return formatPercent(overproductionTotal / collectedTotal)
}

export function formatReportParameter<T extends Provider>(
  provider: T,
  energyType: EnergyType,
): string {
  if (provider.type === ProviderType.HybridHeatPump) {
    return formatHhpSpf(provider, energyType)
  }
  const properties = providerProperties[provider.type]
  const parameterType = properties.reportParameterType
  const formatter = mapParameterTypeToFormatter[parameterType]
  return formatter(provider.parameters)
}

export function formatPrimaryParameter<T extends Provider | ProviderFragment>(
  provider: T,
): string {
  const properties = providerProperties[provider.type]
  const parameterType = properties.primaryParameterType
  const formatter = mapParameterTypeToFormatter[parameterType]
  return formatter(provider.parameters)
}

export function hasSecondaryParameter<T extends Provider | ProviderFragment>(
  provider: T,
): boolean {
  return Boolean(providerProperties[provider.type].getSecondaryParameterType)
}

export function formatSecondaryParameter<T extends Provider>(
  provider: T | undefined,
  energyType: EnergyType,
  providerCalculators?: Provider[],
): string {
  if (!provider) {
    return ''
  }
  const properties = providerProperties[provider.type]
  if (!properties.getSecondaryParameterType) {
    throw new Error(
      `Secondary parameter not implemented for provider of type ${provider.type}`,
    )
  }

  const parameterType = properties.getSecondaryParameterType(energyType)
  if (parameterType === ParameterType.Spf) {
    return formatHhpSpf(provider, energyType)
  }
  if (parameterType === ParameterType.Overproduction) {
    if (!providerCalculators) {
      throw new Error(
        `Unable to calculate secondary parameter for solar panel, missing provider calculators`,
      )
    }
    return formatSolarPanelOverproduction(providerCalculators)
  }
  const formatter = mapParameterTypeToFormatter[parameterType]
  return formatter(provider.parameters)
}

export function getPrimaryParameterTranslationKey(
  providerType: ProviderType,
): string {
  const properties = providerProperties[providerType]
  const parameterType = properties.primaryParameterType
  return `parameterType.${parameterType}`
}

export function getSecondaryParameterTranslationKey(
  providerType: ProviderType,
  energyType: EnergyType,
): string {
  const properties = providerProperties[providerType]
  if (properties.getSecondaryParameterType) {
    const parameterType = properties.getSecondaryParameterType(energyType)
    return `parameterType.${parameterType}`
  }
  throw new Error(
    `Secondary parameter no implemented for provider of type ${providerType}`,
  )
}
